import styled from "@emotion/styled";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, Page, StyledIconButton, Tooltip } from "../../../components";
import { SummaryInfoModal } from "./summaryInfo";
import { NewMigration } from "./newMigration";
import { ExistingMigration } from "./existingMigration";
import { useExistingMigrations } from "../../../hooks";
import { useNewMigration } from "../../../hooks";
import { Plus } from "lucide-react";
import { useAuthContext } from "../authContext";
import ErrorScreen from "./authErrorScreen";

const DisabledButtonWrapper = styled.div`
  float: right;
`;

const Dashboard = () => {
  const [migrationSetId, setMigrationSetId] = useState<number | null>(null);
  const { handleNewMigrationClick } = useNewMigration();
  const { data, isLoading } = useExistingMigrations();
  const { isAuthInitialized, productId } = useAuthContext();
  const navigate = useNavigate();

  // Determine if any migration is not complete
  const isAnyMigrationInProgress = useMemo(() => {
    return data
      ? data.some((migration) => migration.status !== "Complete")
      : false;
  }, [data]);

  if (
    isLoading ||
    !isAuthInitialized ||
    isAnyMigrationInProgress === undefined
  ) {
    return <Loader transparent={false} />;
  }

  if (isAuthInitialized && !productId) {
    return <ErrorScreen />;
  }

  return (
    <>
      <Page
        titleProps={{
          bottomRightContent: data?.length ? (
            <DisabledButtonWrapper>
              <Tooltip
                enabled={isAnyMigrationInProgress}
                message="An import is already in progress."
              >
                <StyledIconButton
                  disabled={isAnyMigrationInProgress}
                  text="New import"
                  icon={<Plus size={20} className="icon" />}
                  onClick={async () => {
                    if (!isAnyMigrationInProgress) {
                      handleNewMigrationClick();
                    }
                  }}
                />
              </Tooltip>
            </DisabledButtonWrapper>
          ) : undefined,
        }}
      >
        {data?.length ? (
          <ExistingMigration
            data={data}
            selectMigration={async (id, status) => {
              if (
                status === "Submitted" ||
                status === "Loading" ||
                status === "Complete"
              ) {
                setMigrationSetId(id);
              } else {
                navigate(`workflow/${id}`);
              }
            }}
          />
        ) : (
          <NewMigration />
        )}
        {!!migrationSetId && (
          <SummaryInfoModal
            migrationSetId={migrationSetId}
            onClose={() => setMigrationSetId(null)}
          />
        )}
      </Page>
    </>
  );
};

export { Dashboard };
